import React, { useState } from 'react';
import { Search as SearchIcon, Bookmark } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { SignalDetail } from './SignalDetail';

interface Result {
  id: number;
  title: string;
  severity?: string | null;
  category?: string | null;
  date?: string;
  prediction?: string;
}

interface SearchResponse {
  query: string | null;
  filters: { tag: string | null; start: string | null; end: string | null };
  counts: Record<string, number>;
  total: number;
  results: Record<string, Result[]>;
}

const TYPE_LABELS: Record<string, string> = {
  signals: 'Signals',
  analyses: 'Analyses',
  predictions: 'Predictions',
};
const SAVED_KEY = 'sentinel.savedSearches';

function loadSaved(): string[] {
  try {
    return JSON.parse(localStorage.getItem(SAVED_KEY) || '[]');
  } catch {
    return [];
  }
}

export function Search() {
  const [input, setInput] = useState('');
  const [nl, setNl] = useState(true);
  const [query, setQuery] = useState('');
  const [openId, setOpenId] = useState<number | null>(null);
  const [saved, setSaved] = useState<string[]>(loadSaved());

  const params = new URLSearchParams();
  if (query) params.set('q', query);
  if (nl) params.set('nl', 'true');

  const { data, loading } = useApi<SearchResponse>(query ? `/api/search?${params}` : '', [query, nl]);

  const run = (q: string) => { setInput(q); setQuery(q); };
  const save = () => {
    if (query && !saved.includes(query)) {
      const next = [query, ...saved].slice(0, 8);
      setSaved(next);
      localStorage.setItem(SAVED_KEY, JSON.stringify(next));
    }
  };

  return (
    <div className="stack" style={{ gap: 'var(--space-6)' }}>
      <div>
        <span className="eyebrow">Intelligence Search</span>
        <h1 className="t-headline-md" style={{ marginTop: 'var(--space-2)' }}>Search</h1>
      </div>

      <form
        className="row"
        style={{ gap: 'var(--space-2)' }}
        onSubmit={(e) => { e.preventDefault(); run(input); }}
      >
        <input
          className="input"
          style={{ flex: 1, minWidth: 240 }}
          placeholder='Try: "how many Bank of Maldives announcements in 2025?"'
          value={input}
          onChange={(e) => setInput(e.target.value)}
        />
        <label className="row" style={{ gap: 4, alignItems: 'center', whiteSpace: 'nowrap' }}>
          <input type="checkbox" checked={nl} onChange={(e) => setNl(e.target.checked)} />
          <span className="t-body-sm">NL</span>
        </label>
        <button type="submit" className="btn btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <SearchIcon size={14} strokeWidth={1.75} /> Search
        </button>
        {query && (
          <button type="button" className="btn btn-secondary" onClick={save} title="Save this search">
            <Bookmark size={14} strokeWidth={1.75} />
          </button>
        )}
      </form>

      {saved.length > 0 && (
        <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
          <span className="t-label-sm" style={{ alignSelf: 'center' }}>Saved:</span>
          {saved.map((s) => (
            <button key={s} className="chip" data-tone="neutral" style={{ cursor: 'pointer' }} onClick={() => run(s)}>
              {s.length > 40 ? s.slice(0, 40) + '…' : s}
            </button>
          ))}
        </div>
      )}

      {loading && <p className="t-body-sm" style={{ color: 'var(--color-text-muted)' }}>Searching…</p>}

      {data && (
        <>
          <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
            <span className="chip" data-tone="info">Total: {data.total}</span>
            {Object.entries(data.counts).map(([t, c]) => (
              <span key={t} className="chip" data-tone="neutral">{TYPE_LABELS[t]}: {c}</span>
            ))}
            {data.filters.tag && <span className="chip" data-tone="warning">#{data.filters.tag}</span>}
            {data.filters.start && (
              <span className="chip" data-tone="neutral">{data.filters.start} → {data.filters.end}</span>
            )}
          </div>

          {data.total === 0 && (
            <p className="t-body-sm" style={{ color: 'var(--color-text-muted)' }}>No matches.</p>
          )}

          {Object.entries(data.results).map(([t, rows]) =>
            rows.length > 0 ? (
              <div key={t} className="stack" style={{ gap: 'var(--space-2)' }}>
                <h2 className="t-title-sm">{TYPE_LABELS[t]} ({data.counts[t]})</h2>
                {rows.map((r) => (
                  <div
                    key={`${t}-${r.id}`}
                    className="card"
                    onClick={t === 'signals' ? () => setOpenId(r.id) : undefined}
                    style={{ cursor: t === 'signals' ? 'pointer' : 'default' }}
                  >
                    <div style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>{r.title}</div>
                    {r.prediction && (
                      <p className="t-body-sm" style={{ color: 'var(--color-text-secondary)' }}>
                        {r.prediction.slice(0, 200)}{r.prediction.length > 200 ? '…' : ''}
                      </p>
                    )}
                    <div className="t-body-sm" style={{ color: 'var(--color-text-muted)' }}>
                      {(r.category || '').replace(/_/g, ' ')}
                      {r.date ? ` · ${new Date(r.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })}` : ''}
                    </div>
                  </div>
                ))}
              </div>
            ) : null
          )}
        </>
      )}

      {openId !== null && <SignalDetail signalId={openId} onClose={() => setOpenId(null)} />}
    </div>
  );
}
